import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { SiteHeader, SiteFooter, Notice } from "../components/Layout";
import { api, fetchBookedSlots } from "../lib/api";
import {
  TIME_BLOCKS, TIME_SLOTS, ALL_SLOTS, OFF_PEAK_SLOTS, PEAK_RATE, OFF_PEAK_RATE,
  rateFor, slotStart, isPastSlot, localISO, prettyDate, blockOf, timeRange, cleanPhone,
} from "../lib/slots";

const DAYS_SHOWN = 14;

function nextDays(n) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i += 1) {
    const day = new Date(d.getFullYear(), d.getMonth(), d.getDate() + i);
    out.push(localISO(day));
  }
  return out;
}

/** The first block that still has a slot to play today, so the page opens on something bookable. */
function firstOpenBlock(date) {
  for (const b of TIME_BLOCKS) {
    if (TIME_SLOTS[b].some((s) => !isPastSlot(date, s))) return b;
  }
  return "evening";
}

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

export default function Book() {
  const [params] = useSearchParams();
  const days = useMemo(() => nextDays(DAYS_SHOWN), []);
  const today = days[0];

  // A slot picked on the home page arrives as ?date=…&slot=…
  const askedDate = params.get("date");
  const askedSlot = params.get("slot");
  const startDate = askedDate && askedDate >= today ? askedDate : today;
  const startBlock = askedSlot && blockOf(askedSlot) ? blockOf(askedSlot) : firstOpenBlock(startDate);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email] = useState("");
  const [bookingDate, setBookingDate] = useState(startDate);
  const [promoCode, setPromoCode] = useState("");
  const [timeBlock, setTimeBlock] = useState(startBlock);
  const [selectedSlots, setSelectedSlots] = useState(
    askedSlot && ALL_SLOTS.includes(askedSlot) && !isPastSlot(startDate, askedSlot) ? [askedSlot] : []
  );
  const [bookedSlots, setBookedSlots] = useState([]);
  const [bookedByDay, setBookedByDay] = useState({});
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoStatus, setPromoStatus] = useState("");
  const [promoLoading, setPromoLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState("");
  const [confirmed, setConfirmed] = useState(null);
  const summaryRef = useRef(null);

  // Free-slot counts for the date strip, fetched once.
  useEffect(() => {
    let cancelled = false;
    Promise.all(days.map((d) => fetchBookedSlots(d).then((b) => [d, b]))).then((rows) => {
      if (!cancelled) setBookedByDay(Object.fromEntries(rows));
    });
    return () => { cancelled = true; };
  }, [days]);

  // Load availability for the chosen day, then keep it fresh by polling.
  // Polling the availability endpoint returns nothing but slot strings, so no
  // booking data is exposed as a live feed.
  useEffect(() => {
    if (!bookingDate) {
      setBookedSlots([]);
      return undefined;
    }
    let cancelled = false;
    const loadSlots = async (showSpinner) => {
      if (showSpinner) setLoadingSlots(true);
      const booked = await fetchBookedSlots(bookingDate);
      if (cancelled) return;
      setBookedSlots(booked);
      setBookedByDay((prev) => ({ ...prev, [bookingDate]: booked }));
      // A slot carried over from the home page may have gone in the meantime.
      setSelectedSlots((sel) => sel.filter((s) => !booked.includes(s)));
      if (showSpinner) setLoadingSlots(false);
    };
    loadSlots(true);
    const poll = setInterval(() => loadSlots(false), 20000);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [bookingDate]);

  // Changing the selection invalidates the server-computed discount, so the
  // code has to be applied again against the new slots.
  useEffect(() => {
    if (!appliedPromo) return;
    setAppliedPromo(null);
    setPromoStatus("⚠️ Selection changed — please apply your promo code again.");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSlots]);

  const fetchAndApplyPromo = async () => {
    const raw = promoCode.trim().toUpperCase();
    if (!raw) {
      setAppliedPromo(null);
      setPromoStatus("");
      return;
    }
    if (selectedSlots.length === 0) {
      setPromoStatus("⚠️ Select at least one slot before applying a promo code.");
      return;
    }
    setPromoLoading(true);
    setPromoStatus("");
    setAppliedPromo(null);
    try {
      // The server decides whether the code is valid and what it is worth.
      // This result is for display only -- /api/book re-validates on its own.
      const result = await api("promo", {
        method: "POST",
        body: { code: raw, slots: selectedSlots, phone: phone.trim(), bookingDate },
      });
      if (!result.valid) {
        setPromoStatus(`❌ ${result.reason}`);
        setAppliedPromo(null);
        return;
      }
      setAppliedPromo({ code: result.code, total: result.total, discount: result.discount });
      setPromoStatus(`✅ Promo code ${result.code} applied!`);
    } catch (err) {
      console.error("Promo validation error:", err);
      setPromoStatus("❌ Failed to validate promo code. Please try again.");
    } finally {
      setPromoLoading(false);
    }
  };

  const peakCount = selectedSlots.filter((s) => !OFF_PEAK_SLOTS.has(s)).length;
  const offPeakCount = selectedSlots.length - peakCount;
  const totalPrice = peakCount * PEAK_RATE + offPeakCount * OFF_PEAK_RATE;

  // Display only. The figure that is actually charged comes back from /api/book.
  const finalPrice = appliedPromo && typeof appliedPromo.total === "number" ? appliedPromo.total : totalPrice;

  const detailsReady = name.trim() !== "" && phone.trim().length === 10 && bookingDate !== "";
  const isBookButtonDisabled = selectedSlots.length === 0 || !paymentMode || !detailsReady;

  // Returns true if booking is NOT allowed due to 11pm cutoff for next-morning bookings
  const isNextMorningCutoffPassed = () => {
    if (!bookingDate || timeBlock !== "morning") return false;
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    if (bookingDate !== localISO(tomorrow)) return false;
    const cutoff = new Date();
    cutoff.setHours(23, 0, 0, 0);
    return now > cutoff;
  };

  const pickDate = (d) => {
    if (d === bookingDate) return;
    setBookingDate(d);
    setSelectedSlots([]);
    setMessage("");
    setConfirmed(null);
    if (d === today) setTimeBlock(firstOpenBlock(d));
  };

  const pickBlock = (b) => {
    if (b === timeBlock) return;
    setTimeBlock(b);
    setSelectedSlots([]);
  };

  const toggleSlot = (slot) => {
    setConfirmed(null);
    setSelectedSlots((sel) => (sel.includes(slot) ? sel.filter((s) => s !== slot) : [...sel, slot]));
  };

  const refresh = async () => {
    setLoadingSlots(true);
    const booked = await fetchBookedSlots(bookingDate);
    setBookedSlots(booked);
    setBookedByDay((prev) => ({ ...prev, [bookingDate]: booked }));
    setLoadingSlots(false);
  };

  const handleBookSlot = async (e) => {
    e.preventDefault();
    if (isBookButtonDisabled || submitting) return;

    // Cutoff rule: block next-day morning bookings after 11pm today
    if (isNextMorningCutoffPassed()) {
      setMessage("❌ Morning bookings for tomorrow are closed after 11:00 pm. Please choose a different time or date.");
      return;
    }

    setSubmitting(true);
    setMessage("");

    // Fetch latest slots right before submitting
    const latestBooked = await fetchBookedSlots(bookingDate);
    const conflict = selectedSlots.some((slot) => latestBooked.includes(slot));
    if (conflict) {
      setBookedSlots(latestBooked);
      setSelectedSlots([]);
      setMessage("❌ One or more slots were just booked by someone else. Please reselect.");
      setSubmitting(false);
      return;
    }

    if (promoCode.trim() && !appliedPromo) {
      setMessage("❌ Please apply your promo code using the Apply button before confirming.");
      setSubmitting(false);
      return;
    }

    try {
      // The server prices the booking and re-checks the promo, the ban list,
      // the 11pm cutoff and slot conflicts. Nothing about money is taken from
      // this form.
      const result = await api("book", {
        method: "POST",
        body: {
          name,
          phone: phone.trim(),
          email,
          bookingDate,
          slots: selectedSlots,
          promoCode: appliedPromo?.code || promoCode.trim() || null,
          paymentMode,
        },
      });

      const confirmedTotal = `₹${result.booking.total_price}`;
      // The server sends the WhatsApp confirmation and says whether it went.
      const whatsappSent = result.whatsapp === true;

      setConfirmed({
        name: name.trim(),
        date: bookingDate,
        range: timeRange(selectedSlots),
        total: confirmedTotal,
        paymentMode,
        whatsappSent,
      });
      setMessage(`✅ Booking confirmed! ${whatsappSent ? 'WhatsApp confirmation sent. ' : ''}Total: ${confirmedTotal}`);

      // Reset the form, keeping the date so the board shows the new booking.
      setName("");
      setPhone("");
      setPromoCode("");
      setAppliedPromo(null);
      setPromoStatus("");
      setSelectedSlots([]);
      setPaymentMode("");
      refresh();
    } catch (error) {
      console.error("Error saving booking:", error);
      // Surface the server's reason (slot taken, cutoff passed, banned, promo
      // rejected) instead of a blanket failure.
      setMessage(`❌ ${error.message || "Booking failed. Please try again."}`);
    } finally {
      setSubmitting(false);
    }
  };

  const freeCount = (d) => {
    const booked = bookedByDay[d];
    if (!booked) return null;
    return ALL_SLOTS.filter((s) => !booked.includes(s) && !isPastSlot(d, s)).length;
  };

  const rateLines = [
    offPeakCount ? `${offPeakCount} × ₹${OFF_PEAK_RATE} off-peak` : null,
    peakCount ? `${peakCount} × ₹${PEAK_RATE} peak` : null,
  ].filter(Boolean);

  const otherDate = !days.includes(bookingDate);

  let hint = "";
  if (!selectedSlots.length) hint = "Pick at least one slot.";
  else if (!detailsReady) hint = "Add your name and 10-digit WhatsApp number.";
  else if (!paymentMode) hint = "Choose how you will pay at the court.";

  return (
    <div className="vv-page vv-page--paper">
      <SiteHeader />

      <main className="vv-wrap vv-book">
        <div className="vv-book__head">
          <h1 className="vv-display vv-h1">Book a court</h1>
          <p className="vv-book__sub">30-minute slots · ₹150 off-peak, ₹250 peak · Pay at the court</p>
        </div>

        <form className="vv-book__grid" onSubmit={handleBookSlot} noValidate>
          <div className="vv-book__main">
            <section aria-labelledby="d-h" className="vv-panel">
              <h2 id="d-h" className="vv-panel__title"><span className="vv-step">1</span> Choose a date</h2>
              <div className="vv-days" role="group" aria-label="Date">
                {days.map((d) => {
                  const on = d === bookingDate;
                  const free = freeCount(d);
                  const [y, m, dd] = d.split("-").map(Number);
                  const dt = new Date(y, m - 1, dd);
                  return (
                    <button
                      key={d}
                      type="button"
                      className={`vv-day ${on ? "is-on" : ""}`}
                      aria-pressed={on}
                      onClick={() => pickDate(d)}
                      disabled={submitting}
                    >
                      <span className="vv-day__wd">{d === today ? "Today" : dt.toLocaleDateString("en-IN", { weekday: "short" })}</span>
                      <span className="vv-day__n">{dt.getDate()}</span>
                      <span className="vv-day__free">
                        {free === null ? dt.toLocaleDateString("en-IN", { month: "short" }) : free === 0 ? "Full" : `${free} free`}
                      </span>
                    </button>
                  );
                })}
              </div>
              <label className={`vv-otherdate ${otherDate ? "is-on" : ""}`}>
                <span>Another date</span>
                <input
                  type="date"
                  className="vv-input vv-input--date"
                  min={today}
                  value={otherDate ? bookingDate : ""}
                  onChange={(e) => e.target.value && pickDate(e.target.value)}
                  disabled={submitting}
                />
              </label>
            </section>

            <section aria-labelledby="s-h" className="vv-panel">
              <div className="vv-panel__headrow">
                <h2 id="s-h" className="vv-panel__title"><span className="vv-step">2</span> Pick your slots</h2>
                <div className="vv-tabs" role="group" aria-label="Time of day">
                  {TIME_BLOCKS.map((b) => (
                    <button
                      key={b}
                      type="button"
                      className={`vv-tab ${timeBlock === b ? "is-on" : ""}`}
                      aria-pressed={timeBlock === b}
                      onClick={() => pickBlock(b)}
                      disabled={submitting}
                    >
                      {cap(b)}
                    </button>
                  ))}
                </div>
              </div>

              {loadingSlots ? (
                <p className="vv-muted">Loading available slots…</p>
              ) : (
                <div className="vv-slots">
                  {TIME_SLOTS[timeBlock].map((slot) => {
                    const booked = bookedSlots.includes(slot);
                    const past = !booked && isPastSlot(bookingDate, slot);
                    const selected = selectedSlots.includes(slot);
                    const unavailable = booked || past;
                    return (
                      <button
                        key={slot}
                        type="button"
                        disabled={unavailable || submitting}
                        onClick={() => !unavailable && toggleSlot(slot)}
                        className={`vv-slot ${selected ? "is-on" : ""} ${unavailable ? "is-off" : ""}`}
                        title={slot}
                        aria-pressed={selected}
                      >
                        <span className="vv-slot__t">{slotStart(slot)}</span>
                        <span className="vv-slot__p">{booked ? "Booked" : past ? "Past" : `₹${rateFor(slot)}`}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="vv-legend">
                <span><i className="vv-legend__free" />Free</span>
                <span><i className="vv-legend__pick" />Your pick</span>
                <span><i className="vv-legend__off" />Booked</span>
                <button type="button" className="vv-linkbtn" onClick={refresh} disabled={loadingSlots}>Refresh</button>
              </div>
            </section>

            <section aria-labelledby="y-h" className="vv-panel">
              <h2 id="y-h" className="vv-panel__title"><span className="vv-step">3</span> Your details</h2>
              <div className="vv-fields">
                <label className="vv-field">
                  <span className="vv-field__label">Full name</span>
                  <input
                    className="vv-input"
                    autoComplete="name"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={submitting}
                    required
                  />
                </label>
                <label className="vv-field">
                  <span className="vv-field__label">WhatsApp number</span>
                  <span className="vv-phone">
                    <span className="vv-phone__cc">+91</span>
                    <input
                      className="vv-input vv-phone__input"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      placeholder="9876543210"
                      value={phone}
                      onChange={(e) => setPhone(cleanPhone(e.target.value))}
                      disabled={submitting}
                      required
                    />
                  </span>
                </label>
              </div>
              <p className="vv-muted vv-small">Your confirmation arrives on this number on WhatsApp.</p>
            </section>
          </div>

          <aside aria-labelledby="sum-h" className="vv-summary" data-vv-surface="dark" id="vv-summary" ref={summaryRef}>
            <h2 id="sum-h" className="vv-display vv-summary__title">Your booking</h2>

            {confirmed && !selectedSlots.length ? (
              <div className="vv-confirmed">
                <p className="vv-confirmed__big vv-display">You're booked</p>
                <p>{prettyDate(confirmed.date, { weekday: "long", day: "numeric", month: "long" })}<br />{confirmed.range}</p>
                <p>Total {confirmed.total}, paid at the court by {confirmed.paymentMode}. {confirmed.whatsappSent ? 'Your WhatsApp confirmation is on its way.' : "We couldn't send a WhatsApp confirmation this time, so please note these details."}</p>
                <Link to="/manage" className="vv-summary__link">Manage or cancel a booking</Link>
              </div>
            ) : (
              <>
                <dl className="vv-summary__rows">
                  <div><dt>Date</dt><dd>{prettyDate(bookingDate) || "—"}</dd></div>
                  <div><dt>Time</dt><dd>{selectedSlots.length ? timeRange(selectedSlots) : "Pick your slots"}</dd></div>
                  <div><dt>Slots</dt><dd>{rateLines.length ? rateLines.join(" · ") : "—"}</dd></div>
                </dl>

                <div className="vv-summary__rule" />

                <label className="vv-field vv-field--on-dark">
                  <span className="vv-field__label">Promo code <span className="vv-optional">(optional)</span></span>
                  <span className="vv-promo">
                    <input
                      className="vv-input"
                      placeholder="Enter code"
                      value={promoCode}
                      onChange={(e) => {
                        setPromoCode(e.target.value);
                        setAppliedPromo(null);
                        setPromoStatus("");
                      }}
                      disabled={submitting || promoLoading}
                    />
                    <button
                      type="button"
                      className="vv-btn vv-btn--light"
                      onClick={fetchAndApplyPromo}
                      disabled={!promoCode.trim() || submitting || promoLoading}
                    >
                      {promoLoading ? "…" : "Apply"}
                    </button>
                  </span>
                </label>
                {promoStatus && <Notice message={promoStatus} />}

                <fieldset className="vv-pay">
                  <legend className="vv-field__label">Pay at the court by</legend>
                  <div className="vv-pay__row">
                    {["UPI", "Cash"].map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        className={`vv-pay__btn ${paymentMode === mode ? "is-on" : ""}`}
                        aria-pressed={paymentMode === mode}
                        onClick={() => setPaymentMode(mode)}
                        disabled={submitting}
                      >
                        {mode}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <div className="vv-summary__total">
                  <span>Total</span>
                  <span className="vv-display">
                    {appliedPromo && finalPrice !== totalPrice && <s className="vv-summary__was">₹{totalPrice}</s>}
                    ₹{finalPrice}
                  </span>
                </div>
                {appliedPromo && (
                  <p className="vv-summary__promo">
                    {finalPrice === 0 ? `Free with ${appliedPromo.code}` : `With ${appliedPromo.code}`}
                  </p>
                )}
              </>
            )}

            {message && !(confirmed && message.startsWith("✅")) && <Notice message={message} />}

            {!(confirmed && !selectedSlots.length) && (
              <>
                <button type="submit" className="vv-btn vv-btn--accent vv-btn--lg vv-btn--block" disabled={isBookButtonDisabled || submitting}>
                  {submitting ? "Booking…" : "Confirm booking"}
                </button>
                {hint && !submitting && <p className="vv-summary__hint">{hint}</p>}
              </>
            )}

            <p className="vv-summary__small">
              Cancel any upcoming booking from <Link to="/manage">Manage booking</Link>. Morning slots for tomorrow close at 11 PM.
            </p>
          </aside>
        </form>
      </main>

      {selectedSlots.length > 0 && (
        <div className="vv-bookbar" data-vv-surface="dark">
          <div className="vv-bookbar__info">
            <span>{prettyDate(bookingDate)} · {timeRange(selectedSlots)}</span>
            <span className="vv-display vv-bookbar__price">₹{finalPrice}</span>
          </div>
          <button
            type="button"
            className="vv-btn vv-btn--accent vv-btn--block"
            onClick={() => summaryRef.current && summaryRef.current.scrollIntoView({ behavior: "smooth", block: "start" })}
          >
            Review and confirm
          </button>
        </div>
      )}

      <SiteFooter />
    </div>
  );
}
